import B_Define from '../../../base/dev/util/B_Define';
import B_Util from '../../../base/dev/util/B_Util';
import R_Define from './R_Define';
import BlocklyManager from '../../../../../../../../../assets/js/codmos/BlocklyManager';
import R_Parser from './R_Parser';

const init = () => {
    return class R_Interpreter {
        constructor(cm) {
            this.CODMOS = cm;

            this._runningSet = new Set();

            this._windowEvtList = [];

            this._whileList = [];

            this.hitEvt = {};
        }

        // 코드 실행 시 scope 내에서 값을 찾음
        get scope() {
            const scope = {
                gameOver: (...arg) => { this.CODMOS.WORLD.gameOver(...arg); },
                setBG: (...arg) => { this.CODMOS.WORLD.setBG(...arg); },
                hero: Object.values(this.CODMOS.WORLD.LIST[R_Define.HERO_TYPE])[0]
            };

            // 모든 변수 객체 스코프에 등록
            const VARIABLE_LIST = this.CODMOS.WORLD.LIST[B_Define.VARIABLE_REAL_TYPE];
            Object.keys(VARIABLE_LIST)
                .forEach((key) => { scope[key] = VARIABLE_LIST[key]; });

            return scope;
        }

        /**
         * [ Function :: setSpeed ]
         *
         * @DES :: 속도 설정
         * @IP1 :: speed {number|string}
         * @IP1 :: cb {function}
         * @O.P :: null
         */
        setSpeed(speed, cb) {
            // 랜덤
            if (speed === 'random') speed = R_Define.SPEED_LEVEL[B_Util.getRandomInt(0, R_Define.SPEED_LEVEL.length - 1)];

            // 만약 '속도' 변수 존재할 경우 값 변경
            if (this.scope[R_Define.VARIABLES.SPEED]) this.scope[R_Define.VARIABLES.SPEED].setValue(speed);

            this.CODMOS.speed = speed;
            if (typeof cb === 'function') cb();
        }

        /**
         * [ Function :: clear ]
         *
         * @DES :: 윈도우 이벤트 제거
         * @O.P :: null
         */
        clear() {
            this._windowEvtList.forEach(([type, func]) => {
                window.removeEventListener(type, func);
            });
            this._whileList.forEach(e => clearInterval(e));
        }

        end() {
            this.clear();
            this.isEnd = true;
        }

        /**
         * [ Function :: _addEvt ]
         *
         * @DES :: 윈도우 이벤트 등록
         * @IP1 :: type {string}
         * @IP2 :: func {function}
         * @O.P :: null
         */
        _addEvt(type, func) {
            this._windowEvtList.push([type, func]);
            window.addEventListener(type, func);
        }

        /**
         * [ Function :: init ]
         *
         * @DES :: 블록 -> 코드 등록 및 실행
         * @O.P :: null
         */
        init() {
            this.CODMOS.TOOL.onTimer(() => {
                BlocklyManager.whenBlocks.forEach((e) => {
                    const code = new R_Parser(this.getCode(e, 'highlightBlock(%1);'));

                    if (e === 'when_press_start_button') { // 시작 블록일 경우 바로 실행
                        this.run(code);
                    } else if (e.startsWith('when_click_')) {
                        // this.run(code);
                    } else if (e.startsWith('when_press_')) { // 키보드 블록 이벤트 등록
                        const blockObj = BlocklyManager.kybrdBlck.find(e2 => e2.name === e);

                        if (blockObj) {
                            let evtType;
                            if (e.startsWith('when_press_up_')) {
                                evtType = 'keyup';
                            } else {
                                evtType = 'keydown';
                            }
                            this._addEvt(evtType, async (evt) => {
                                if (
                                    evt.keyCode === blockObj.keyCode
                                    && !this._runningSet.has(e)
                                ) {
                                    this._runningSet.add(e);
                                    await this.run(code);
                                    this._runningSet.delete(e);
                                }
                            });
                        }
                    } else if (e.startsWith('when_hit_')) { // 충돌 이벤트 저장
                        let target = e.replace('when_hit_', '');

                        if (target === 'obstacle') { // 장애물에 닿았을 때
                            this.hitObstacle = () => { this.run(code); };
                        } else { // 아이템에 닿았을 때
                            target = target.replace('item_', '');
                            this.hitEvt[target] = () => { this.run(code); };
                        }
                    }
                });
            });
        }

        /**
         * [ Function :: hit ]
         *
         * @DES :: 아이템 충돌 이벤트
         * @IP1 :: res {string}
         * @O.P :: null
         */
        hit(res) {
            this.scope.hero.hitItemCount++;
            const evt = this.hitEvt[res];
            if (typeof evt === 'function') evt();
        }

        /**
         * [ Function :: getCode ]
         *
         * @DES :: 블록명으로 코드 추출
         * @IP1 :: block {string}
         * @IP2 :: prefix {string}
         * @O.P :: {string}
         */
        getCode(block, prefix) {
            return BlocklyManager.getBlockCode(block, prefix);
        }

        /**
         * [ Function :: parse ]
         *
         * @DES :: 텍스트 코드를 인터프리터 형식에 맞게 변환
         * @IP1 :: code {string}
         * @O.P :: codeSet {object}
         */
        parse(code) {
            // 괄호
            let openParameter = false;
            // 따옴표
            let openQuote = false;
            let quoteType = '';
            // if
            let openIf = false;
            // if 문 내부에서 또 다시 if 문 등이 열릴 때
            let scopeOnIf = false;
            let scopeOnIfCnt = 0;
            // if 문 괄호
            let openIfBracket = false;
            // if 문 화살괄호
            let openIfScope = false;
            let openIfScopeCode = '';
            // else 문 화살괄호
            let openElseScope = false;
            let openElseScopeCode = '';

            const codeSet = [];
            let codeObj = {
                target: '',
                parameter: []
            };
            let parameterIdx = 0;

            const parameterAppend = (e) => {
                if (!codeObj.parameter[parameterIdx]) {
                    codeObj.parameter[parameterIdx] = {
                        type: openQuote ? 'string' : 'number',
                        value: ''
                    };
                }

                codeObj.parameter[parameterIdx].value += e;
            };

            const endIf = () => {
                codeObj.scopeCode = this.parse(openIfScopeCode);
                openIfScopeCode = '';
                scopeOnIf = false;
                scopeOnIfCnt = 0;
                openIfScope = false;
                openIf = false;

                codeSet.push(codeObj);
                codeObj = {
                    target: '',
                    parameter: []
                };
            };

            const codes = Array.from(code);

            while (codes.length) {
                const e = codes.shift();

                if (openElseScope) {
                    switch (e) {
                        case '}': {
                            if (!openQuote) {
                                codeObj.elseCode = this.parse(openElseScopeCode);
                                openElseScopeCode = '';
                                openElseScope = false;
                                endIf();
                            } else {
                                openElseScopeCode += e;
                            }
                            break;
                        }
                        case '\'':
                        case '`':
                        case '"': {
                            if (openQuote) {
                                if (quoteType === e) {
                                    openQuote = false;
                                }
                            } else {
                                openQuote = true;
                                quoteType = e;
                            }
                            openElseScopeCode += e;
                            break;
                        }
                        default: {
                            openElseScopeCode += e;
                        }
                    }
                } else if (openIfScope) {
                    switch (e) {
                        case '}': {
                            if (!openQuote) {
                                scopeOnIfCnt--;
                                if (!scopeOnIf || scopeOnIfCnt < 0) {
                                    if (codes.slice(0, 5).join('').trim() === 'else') {
                                        codes.splice(0, 7);
                                        openElseScope = true;
                                    } else {
                                        endIf();
                                    }
                                } else {
                                    openIfScopeCode += e;
                                }
                            } else {
                                openIfScopeCode += e;
                            }
                            break;
                        }
                        case '\'':
                        case '`':
                        case '"': {
                            if (openQuote) {
                                if (quoteType === e) {
                                    openQuote = false;
                                }
                            } else {
                                openQuote = true;
                                quoteType = e;
                            }
                            openIfScopeCode += e;
                            break;
                        }
                        case 'i': {
                            if (!openQuote && codes[0] === 'f') {
                                scopeOnIf = true;
                                scopeOnIfCnt++;
                            }
                            openIfScopeCode += e;
                            break;
                        }
                        default: {
                            openIfScopeCode += e;
                        }
                    }
                } else if (openParameter) {
                    switch (e) {
                        case ')': {
                            if (
                                (
                                    codeObj.parameter[parameterIdx]
                                    && codeObj.parameter[parameterIdx].value.endsWith('].getValue(')
                                )
                                || openQuote
                            ) {
                                parameterAppend(e);
                            } else {
                                openParameter = false;
                            }
                            break;
                        }
                        case ',': {
                            if (openQuote) {
                                parameterAppend(e);
                            } else {
                                parameterIdx++;
                            }
                            break;
                        }
                        case '\'':
                        case '`':
                        case '"': {
                            if (openQuote) {
                                if (quoteType === e) {
                                    openQuote = false;
                                } else {
                                    parameterAppend(e);
                                }
                            } else {
                                openQuote = true;
                                quoteType = e;
                            }
                            break;
                        }
                        default: {
                            parameterAppend(e);
                        }
                    }
                } else if (openIfBracket) {
                    switch (e) {
                        case ')': {
                            if (codeObj.test.endsWith('\'].getValue(')) {
                                codeObj.test += e;
                            } else {
                                openIfBracket = false;
                            }
                            break;
                        }
                        default: {
                            codeObj.test += e.trim();
                        }
                    }
                } else if (openIf) {
                    switch (e) {
                        case '(': {
                            openIfBracket = true;
                            break;
                        }
                        case '{': {
                            openIfScope = true;
                            break;
                        }
                        default: {
                            codeObj.test += e.trim();
                        }
                    }
                } else {
                    switch (e) {
                        case ';': {
                            codeSet.push(codeObj);

                            // 초기화
                            codeObj = {
                                target: '',
                                parameter: []
                            };
                            parameterIdx = 0;
                            break;
                        }
                        case '(': {
                            openParameter = true;
                            break;
                        }
                        case 'f': {
                            if (codeObj.target.length === 1 && codeObj.target === 'i') {
                                codeObj.target = 'if';
                                codeObj.test = '';
                                openIf = true;
                            } else {
                                codeObj.target += e;
                            }
                            break;
                        }
                        default: {
                            codeObj.target += e.trim();
                        }
                    }
                }
            }

            return codeSet;
        }

        /**
         * [ Function :: _getValue ]
         *
         * @DES :: 파라미터 객체의 값 추출
         * @IP1 :: value {string}
         * @O.P :: value {any}
         */
        _getValue(value) {
            if (value.startsWith('this[')) {
                value = this.scope[value.replace(/this|\[|]|\.|getValue\(\)|'/g, '')].value;
            } else {
                const parseValue = Number(value);
                if (!Number.isNaN(parseValue)) value = parseValue;
            }
            return value;
        }

        /**
         * [ Function :: highlightBlock ]
         *
         * @DES :: 블록 하이라이트 처리
         * @IP1 :: id {string}
         * @IP2 :: cb {function}
         * @O.P :: null
         */
        highlightBlock(id, cb) {
            Blockly.mainWorkspace.highlightBlock(id);
            cb();
        }

        /**
         * [ Function :: playSound ]
         *
         * @DES :: 소리 재생
         * @IP1 :: src {string}
         * @IP2 :: cb {function}
         * @O.P :: null
         */
        playSound(src, cb) {
            if (window.LC_VOLUME) {
                const audio = new Audio(R_Define.SOUND_LIST[src]);
                audio.play();
            }
            cb();
        }

        /**
         * [ Function :: run ]
         *
         * @DES :: 코드 실행
         * @IP1 :: codeSet {object|string}
         * @O.P :: null
         */
        async run(codeSet) {
            const { scope } = this;
            if (!this.isEnd) {
                if (typeof codeSet === 'string') {
                    codeSet = new R_Parser(codeSet);
                }

                await codeSet.reduce(async (a, b) => {
                    await a;

                    return new Promise(async (resolve, reject) => {
                        try {
                            if (!this.isEnd) {
                                const parameter = (b.parameter || []).map((e) => {
                                    let value = this._getValue(e.value);
                                    if (e.type === 'number') {
                                        value = Number(value);
                                    }
                                    return value;
                                });

                                const cb = () => { if (!this.isEnd) resolve(); };

                                switch (b.target) {
                                    case 'playSound2': {
                                        const audio = new Audio(R_Define.SOUND_LIST[parameter[0]]);
                                        audio.onended = cb;
                                        audio.volume = window._AUDIO_STATUS ? window._AUDIO_VOLUME / 100 : 0;
                                        audio.play();
                                        break;
                                    }
                                    case 'if': {
                                        let operator;
                                        if (b.test.includes('>')) {
                                            operator = '>';
                                        } else if (b.test.includes('==')) {
                                            operator = '==';
                                        } else if (b.test.includes('<')) {
                                            operator = '<';
                                        }

                                        
                                        let [first, last] = b.test.split(operator);
                                        first = this._getValue(first);
                                        last = this._getValue(last);

                                        let bool = false;
                                        switch (operator) {
                                            case '>': {
                                                if (first > last) bool = true;
                                                break;
                                            }
                                            case '==': {
                                                if (first === last) bool = true;
                                                break;
                                            }
                                            case '<': {
                                                if (first < last) bool = true;
                                                break;
                                            }
                                            default:
                                        }

                                        if (bool) {
                                            await this.run(b.scopeCode);
                                            cb();
                                        } else if (b.elseCode) {
                                            await this.run(b.elseCode);
                                            cb();
                                        } else {
                                            cb();
                                        }
                                        break;
                                    }
                                    case 'while': {
                                        await this.run(b.scopeCode);
                                        let isWhile = false;
                                        const interval = setInterval(async () => {
                                            if (!isWhile) {
                                                isWhile = true;
                                                await this.run(b.scopeCode);
                                                isWhile = false;
                                            }
                                        }, R_Define.WHILE_INTERVAL);
                                        this._whileList.push(interval);
                                        break;
                                    }
                                    default: {
                                        let target = b.target.split('.');
                                        const func = target.pop();

                                        switch (func) {
                                            case 'getValue':
                                            case 'setValue':
                                            case 'plusValue':
                                            case 'minusValue': {
                                                target = target[0].replace('this', '').replace(/\[|\]|'/gi, '');
                                                scope[target][func](...parameter);
                                                switch (target) {
                                                    case R_Define.VARIABLES.SPEED: {
                                                        const speed = scope[target].getValue(...parameter);
                                                        this.setSpeed(speed);
                                                        break;
                                                    }
                                                    default:
                                                }
                                                cb();
                                                break;
                                            }
                                            default: {
                                                if (target.length) {
                                                    target.reduce(
                                                        (a2, b2) => a2[b2], scope
                                                    )[func](...parameter, cb);
                                                } else if (this[func]) {
                                                    this[func](...parameter, cb);
                                                } else {
                                                    scope[func](...parameter, cb);
                                                }
                                            }
                                        }
                                    }
                                }
                            } else {
                                resolve();
                            }
                        } catch (error) {
                            reject(error);
                        }
                    });
                }, Promise.resolve);
            }
        }
    };
};

export default init();



// WEBPACK FOOTER //
// ./src/assets/js/codmos/game/lcPlugIn/canvas/runner/dev/util/R_Interpreter.js